import { useEffect, useRef } from "react";
import * as THREE from "three";

const GLYPHS = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];
const GOLD = 0xc9a25e;

function glyphSprite(glyph) {
  const cv = document.createElement("canvas");
  cv.width = cv.height = 128;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#C9A25E";
  ctx.font = "84px 'Cormorant Garamond', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(glyph + "\uFE0E", 64, 68);
  const mat = new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(cv), transparent: true, opacity: 0.55, depthWrite: false });
  const s = new THREE.Sprite(mat);
  s.scale.set(0.55, 0.55, 1);
  return s;
}

function ring(radius, opacity, tube = 0.006) {
  return new THREE.Mesh(
    new THREE.TorusGeometry(radius, tube, 8, 160),
    new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity })
  );
}

function buildWheel() {
  const wheel = new THREE.Group();
  wheel.add(ring(3.4, 0.35), ring(3.0, 0.18), ring(2.2, 0.22), ring(1.2, 0.18));
  const spokeMat = new THREE.LineBasicMaterial({ color: GOLD, transparent: true, opacity: 0.14 });
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    const g = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(Math.cos(a) * 2.2, Math.sin(a) * 2.2, 0),
      new THREE.Vector3(Math.cos(a) * 3.4, Math.sin(a) * 3.4, 0),
    ]);
    wheel.add(new THREE.Line(g, spokeMat));
    // Counter-clockwise from the left (Ascendant), as on a natal chart
    const ga = Math.PI + ((i * 30 + 15) * Math.PI) / 180;
    const sp = glyphSprite(GLYPHS[i]);
    sp.position.set(Math.cos(ga) * 2.6, Math.sin(ga) * 2.6, 0);
    wheel.add(sp);
  }
  return wheel;
}

function buildStars() {
  const pts = new Float32Array(600 * 3);
  for (let i = 0; i < pts.length; i++) pts[i] = (Math.random() - 0.5) * 16;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pts, 3));
  return new THREE.Points(g, new THREE.PointsMaterial({ color: 0xb79bc7, size: 0.025, transparent: true, opacity: 0.6 }));
}

export default function Zodiac3D() {
  const mount = useRef(null);

  useEffect(() => {
    const el = mount.current;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 9);
    const wheel = buildWheel();
    wheel.rotation.x = -0.95;
    const stars = buildStars();
    scene.add(wheel, stars);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = el;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);
    const mouse = { x: 0, y: 0 };
    const onMove = (e) => { mouse.x = e.clientX / window.innerWidth - 0.5; mouse.y = e.clientY / window.innerHeight - 0.5; };
    window.addEventListener("mousemove", onMove);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (document.hidden || window.scrollY > window.innerHeight) return;
      if (!reduce) {
        wheel.rotation.z += 0.0012;
        stars.rotation.y += 0.0003;
      }
      wheel.rotation.x += (-0.95 + mouse.y * 0.25 - wheel.rotation.x) * 0.04;
      wheel.rotation.y += (mouse.x * 0.3 - wheel.rotation.y) * 0.04;
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      scene.traverse((o) => { o.geometry?.dispose(); o.material?.map?.dispose(); o.material?.dispose(); });
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mount} data-testid="zodiac-3d" className="absolute inset-0 pointer-events-none opacity-70" />;
}
